import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {InteractionTypesEnum} from '../../../../libs/shared/helpers/src/interaction/interactionTypes';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {CenteredPopover} from '../../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {PopoverProps} from '../../../../libs/shared/ui/src/components/popovers/popoverTypes';
import {useInteractionContext} from '../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {InteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {useInteractionTracking} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';
import {shadows} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {useSaveAndRestoreFocusEffect} from '../../../helpers/focusHelpers';
import {Modal} from '../modals/modal';

/*
 * Props.
 */

export interface DialogProps extends PopoverProps {
  /** The width of the dialog. */
  width?: number;
  /** The height of the dialog. */
  height?: number;
  /** The minimum width of the dialog. */
  minWidth?: string;
  /** The minimum height of the dialog. */
  minHeight?: string;
  /** The maximum height the dialog. */
  maxHeight?: string;
  interactionId?: string;
  className?: string;
}

/*
 * Constants.
 */

const defaultWidth = 420;

/*
 * Style.
 */

interface DialogStyleProps {
  $width?: number;
  $height?: number;
  $minWidth?: string;
  $minHeight?: string;
  $maxHeight?: string;
}
const StyledModal = styled(Modal)<DialogStyleProps>`
  display: flex;
  flex-direction: column;

  width: ${(p) => p.$width || defaultWidth}px;
  ${(p) => p.$height && `height: ${p.$height}px`};
  ${(p) => p.$minWidth && `min-width: ${p.$minWidth}`};
  ${(p) => p.$minHeight && `min-height: ${p.$minHeight}`};
  ${(p) => p.$maxHeight && `max-height: ${p.$maxHeight}`};

  overflow: hidden;
  border-radius: 16px;
  color: ${(p) => p.theme.text};
  background-color: ${(p) => p.theme.foreground};
  ${shadows.dialogsAndDropdowns};
`;

const StyledFocusSpan = styled.span`
  display: none;
`;

/*
 * Component.
 */

export const Dialog: FC<DialogProps> = (props) => {
  const {trackInteraction} = useInteractionTracking();
  const saveAndRestoreFocusContextRef = useRef<HTMLElement>(null);
  useSaveAndRestoreFocusEffect(saveAndRestoreFocusContextRef);

  const {onRequestClose} = props;
  const keyHandlers = {
    [KeyboardActionsEnum.CLOSE]: () => {
      if (onRequestClose) {
        onRequestClose();
      }
    },
  };

  const interactionId = props.interactionId || 'dialog';
  const interactionContext = useInteractionContext();

  useInitialEffect(() => {
    trackInteraction(
      InteractionTypesEnum.DIALOG_SHOWN,
      interactionId,
      interactionContext.parentInteractionIds,
    );
  });

  return (
    <KeyboardShortcuts handlers={keyHandlers} requestExclusivity>
      <InteractionParent interactionId={interactionId}>
        <CenteredPopover {...props} shouldPreventHorizontalOverflow isExclusive>
          <StyledModal
            interactionId={interactionId}
            $width={props.width}
            $height={props.height}
            $minWidth={props.minWidth}
            $minHeight={props.minHeight}
            $maxHeight={props.maxHeight}
            className={props.className}
          >
            {props.children}
          </StyledModal>
        </CenteredPopover>
        {/* We need a reference to an element outside the popover portal. */}
        <StyledFocusSpan ref={saveAndRestoreFocusContextRef} />
      </InteractionParent>
    </KeyboardShortcuts>
  );
};
